import { useCustom, useCustomMutation, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Table,
  Typography,
} from 'antd';
import { format } from 'date-fns';
import { useState } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';

/**
 * E33 · Backoffice — Members, with its two sections: Grant access (by e-mail — the account must
 * already exist) and Revoke on a member's row (never the last member: the screen says so instead of
 * offering the button). Every act is recorded with the signed-in member as its performer.
 */
type Member = {
  subject: string;
  email: string | null;
  displayName: string | null;
  grantedBy: string | null;
  grantedByEmail: string | null;
  grantedAt: string;
  isLast: boolean;
};

const MEMBER_FIELDS = [
  'subject',
  'email',
  'displayName',
  'grantedBy',
  'grantedByEmail',
  'grantedAt',
  'isLast',
];

export const BackofficeMembers = () => {
  const translate = useTranslate();
  const { message } = AntdApp.useApp();
  const [email, setEmail] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const { data, isFetching, refetch } = useCustom<Member[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'backofficeMembers', fields: MEMBER_FIELDS },
  });
  const members = data?.data ?? [];
  const { mutateAsync, isLoading: saving } = useCustomMutation<Member[]>();

  const act = async (
    operation: 'grantBackofficeAccess' | 'revokeBackofficeAccess',
    variables: Record<string, { name: string; type: string; value: string }>,
    done: string,
  ) => {
    setFailure(null);
    try {
      await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: { operation, fields: MEMBER_FIELDS, variables },
      });
      message.success(translate(done));
      await refetch();
      return true;
    } catch (error) {
      // the server's own coded message is already the operator's words (no such account, last member…)
      setFailure(
        graphqlErrorInfo(error).message ||
          translate('backoffice.members.failed'),
      );
      return false;
    }
  };

  const grant = async () => {
    if (!email.trim()) return;
    const ok = await act(
      'grantBackofficeAccess',
      { email: { name: 'email', type: 'String!', value: email.trim() } },
      'backoffice.members.granted',
    );
    if (ok) setEmail('');
  };

  const day = (iso: string) => format(new Date(iso), 'yyyy-MM-dd');

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3}>
          {translate('backoffice.members.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {translate('backoffice.members.intro')}
        </Typography.Paragraph>
      </div>
      {failure ? (
        <Alert
          type="error"
          showIcon
          message={failure}
          data-testid="backoffice-members-failure"
        />
      ) : null}
      <Table<Member>
        scroll={{ x: 'max-content' }}
        rowKey="subject"
        loading={isFetching}
        dataSource={members}
        pagination={false}
        data-testid="backoffice-members-table"
        onRow={(m) =>
          ({ 'data-testid': `backoffice-member-${m.subject}` } as never)
        }
        columns={[
          { title: translate('backoffice.members.email'), dataIndex: 'email' },
          {
            title: translate('backoffice.members.name'),
            dataIndex: 'displayName',
          },
          {
            title: translate('backoffice.members.grantedBy'),
            render: (_, m) =>
              m.grantedBy
                ? m.grantedByEmail ?? m.grantedBy
                : translate('backoffice.members.operator'),
          },
          {
            title: translate('backoffice.members.grantedAt'),
            render: (_, m) => day(m.grantedAt),
          },
          {
            title: translate('table.actions'),
            render: (_, m) =>
              m.isLast ? (
                <Typography.Text
                  type="secondary"
                  data-testid="backoffice-last-member-note"
                >
                  {translate('backoffice.members.lastMember')}
                </Typography.Text>
              ) : (
                <Popconfirm
                  title={translate('backoffice.members.revokeConfirm')}
                  okButtonProps={{ danger: true }}
                  okText={translate('backoffice.members.revoke')}
                  onConfirm={() =>
                    act(
                      'revokeBackofficeAccess',
                      {
                        subject: {
                          name: 'subject',
                          type: 'String!',
                          value: m.subject,
                        },
                      },
                      'backoffice.members.revoked',
                    )
                  }
                >
                  <Button
                    danger
                    size="small"
                    data-testid={`backoffice-revoke-${m.subject}`}
                  >
                    {translate('backoffice.members.revoke')}
                  </Button>
                </Popconfirm>
              ),
          },
        ]}
      />
      <Card title={translate('backoffice.members.grantTitle')}>
        <Typography.Paragraph type="secondary">
          {translate('backoffice.members.grantHint')}
        </Typography.Paragraph>
        <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
          <Input
            type="email"
            value={email}
            placeholder={translate('backoffice.members.grantPlaceholder')}
            onChange={(e) => setEmail(e.target.value)}
            onPressEnter={grant}
            data-testid="backoffice-grant-email"
          />
          <Button
            type="primary"
            loading={saving}
            disabled={!email.trim()}
            onClick={grant}
            data-testid="backoffice-grant-button"
          >
            {translate('backoffice.members.grant')}
          </Button>
        </Space.Compact>
      </Card>
    </Space>
  );
};
